import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ApprovalConfigApprover,
  ApprovalConfigDetail,
  ApprovalConfigStep,
} from '../../../../../core/models/approval/approval-config.model';
import { MenuListOption } from '../../../../../core/models/system-settings/menu-lists.model';
import { AccessRole } from '../../../../../core/models/system-settings/manage-roles.model';
import { MapperApprovalConfigService } from '../../../../../core/mappers/approval/mapper-approval-config.service';
import { ApiApprovalConfigService } from '../../../../../core/services/api/approval/api-approval-config.service';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperManageRolesService } from '../../../../../core/mappers/system-settings/mapper-manage-roles.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import {
  PssSearchablePickerComponent,
  SearchablePickerOption,
} from '../../../../../tools/template/pss/pss-searchable-picker/pss-searchable-picker.component';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

type PickerKind = 'role' | 'user' | 'department';

@Component({
  selector: 'app-modal-approval-config',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSearchablePickerComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-approval-config.component.html',
  styleUrl: './modal-approval-config.component.css',
})
export class ModalApprovalConfigComponent implements OnInit {
  @Input() configId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  private static readonly OPTIONS_PREVIEW = 20;

  formData!: ApprovalConfigDetail;
  menuOptions: MenuListOption[] = [];
  roles: AccessRole[] = [];
  isLoading = signal(false);
  pickerOptions = signal<SearchablePickerOption[]>([]);
  pickerTotal = signal(0);
  pickerLoading = signal(false);

  approverTypes = [
    { value: 'role', label: 'Role' },
    { value: 'user', label: 'ผู้ใช้ (User)' },
    { value: 'department', label: 'แผนก (Department)' },
  ];
  nextModes = [
    { value: 'end', label: 'จบหลังชั้นนี้' },
    { value: 'fixed', label: 'ส่งต่ออัตโนมัติ (Fixed)' },
    { value: 'select_by_approver', label: 'ผู้อนุมัติเลือกคนถัดไป' },
  ];
  triggerOptions = [
    { value: 'create', label: 'เมื่อสร้างรายการ' },
    { value: 'update', label: 'เมื่อแก้ไขรายการ' },
  ];

  private activePickerKey: string | null = null;
  private pickerSearchText = '';
  private poolTypes = new WeakMap<ApprovalConfigApprover, PickerKind>();

  get showApproverPool(): boolean {
    return this.formData.steps.some((s) => s.nextApproverMode === 'select_by_approver');
  }

  private readonly api = inject(ApiApprovalConfigService);
  private readonly settingsApi = inject(ApiSystemSettingsService);
  private readonly mapper = inject(MapperApprovalConfigService);
  private readonly rolesMapper = inject(MapperManageRolesService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  readonly siteState = inject(SiteStateService);

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyDetail();
    this.loadLookups();
    if (this.configId > 0) {
      this.loadDetail(this.configId);
    } else {
      this.syncSteps();
      const siteId = this.siteState.selectedSite()?.id ?? null;
      this.formData.siteId = siteId;
    }
  }

  stepPickerKey(stepNumber: number, kind: PickerKind): string {
    return `step:${stepNumber}:${kind}`;
  }

  poolPickerKey(index: number, kind: PickerKind): string {
    return `pool:${index}:${kind}`;
  }

  onApproverTypeChange(step: ApprovalConfigStep): void {
    if (step.approverType !== 'role') {
      step.roleId = null;
      step.roleName = '';
    }
    if (step.approverType !== 'user') {
      step.userId = null;
      step.userName = '';
    }
    if (step.approverType !== 'department') {
      step.departmentId = null;
      step.departmentName = '';
    }
  }

  poolKind(pool: ApprovalConfigApprover): PickerKind {
    const stored = this.poolTypes.get(pool);
    if (stored) return stored;
    if (pool.userId) return 'user';
    if (pool.departmentId) return 'department';
    return 'role';
  }

  onPoolKindChange(pool: ApprovalConfigApprover, kind: PickerKind): void {
    this.poolTypes.set(pool, kind);
    if (kind !== 'role') {
      pool.roleId = null;
      pool.roleName = '';
    }
    if (kind !== 'user') {
      pool.userId = null;
      pool.userName = '';
    }
    if (kind !== 'department') {
      pool.departmentId = null;
      pool.departmentName = '';
    }
  }

  private syncPoolTypes(): void {
    for (const pool of this.formData.approvers) {
      if (pool.userId) this.poolTypes.set(pool, 'user');
      else if (pool.departmentId) this.poolTypes.set(pool, 'department');
      else if (pool.roleId) this.poolTypes.set(pool, 'role');
      else if (!this.poolTypes.has(pool)) this.poolTypes.set(pool, 'role');
    }
  }

  onPickerOpened(key: string): void {
    this.activePickerKey = key;
    this.pickerSearchText = '';
    this.loadPickerOptions('');
  }

  onPickerSearch(key: string, text: string): void {
    this.activePickerKey = key;
    this.pickerSearchText = text;
    this.loadPickerOptions(text);
  }

  onPickerSelected(key: string, option: SearchablePickerOption): void {
    const parsed = this.parsePickerKey(key);
    if (!parsed) return;

    if (parsed.scope === 'step') {
      const step = this.formData.steps.find((s) => s.stepNumber === parsed.index);
      if (!step) return;
      if (parsed.kind === 'role') {
        step.roleId = option.id;
        step.roleName = option.label;
      } else if (parsed.kind === 'user') {
        step.userId = option.id;
        step.userName = option.label;
      } else {
        step.departmentId = option.id;
        step.departmentName = option.label;
      }
      return;
    }

    const pool = this.formData.approvers[parsed.index];
    if (!pool) return;
    if (parsed.kind === 'role') {
      pool.roleId = option.id;
      pool.roleName = option.label;
    } else if (parsed.kind === 'user') {
      pool.userId = option.id;
      pool.userName = option.label;
    } else {
      pool.departmentId = option.id;
      pool.departmentName = option.label;
    }
  }

  onPickerCleared(key: string): void {
    const parsed = this.parsePickerKey(key);
    if (!parsed) return;

    if (parsed.scope === 'step') {
      const step = this.formData.steps.find((s) => s.stepNumber === parsed.index);
      if (!step) return;
      if (parsed.kind === 'role') {
        step.roleId = null;
        step.roleName = '';
      } else if (parsed.kind === 'user') {
        step.userId = null;
        step.userName = '';
      } else {
        step.departmentId = null;
        step.departmentName = '';
      }
      return;
    }

    const pool = this.formData.approvers[parsed.index];
    if (!pool) return;
    if (parsed.kind === 'role') {
      pool.roleId = null;
      pool.roleName = '';
    } else if (parsed.kind === 'user') {
      pool.userId = null;
      pool.userName = '';
    } else {
      pool.departmentId = null;
      pool.departmentName = '';
    }
  }

  stepPickerId(step: ApprovalConfigStep, kind: PickerKind): number | null {
    if (kind === 'role') return step.roleId;
    if (kind === 'user') return step.userId;
    return step.departmentId;
  }

  stepPickerLabel(step: ApprovalConfigStep, kind: PickerKind): string {
    if (kind === 'role') return step.roleName;
    if (kind === 'user') return step.userName;
    return step.departmentName;
  }

  poolPickerId(pool: ApprovalConfigApprover, kind: PickerKind): number | null {
    if (kind === 'role') return pool.roleId;
    if (kind === 'user') return pool.userId;
    return pool.departmentId;
  }

  poolPickerLabel(pool: ApprovalConfigApprover, kind: PickerKind): string {
    if (kind === 'role') return pool.roleName;
    if (kind === 'user') return pool.userName;
    return pool.departmentName;
  }

  onClose(): void {
    this.close.emit();
  }

  onMaxLevelChange(): void {
    this.syncSteps();
  }

  onSave(): void {
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อ config');
      return;
    }
    if (!this.formData.menuListId) {
      this.toast.error('กรุณาเลือกเมนู');
      return;
    }

    const stepError = this.validateSteps();
    if (stepError) {
      this.toast.error(stepError);
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const isEdit = this.formData.id > 0;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const payload = this.mapper.mapToSavePayload(this.formData, userId, menuId);

    this.isLoading.set(true);
    this.api.saveApprovalConfig(payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.close.emit();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาด');
        this.isLoading.set(false);
      },
    });
  }

  addApprover(): void {
    const nextFor = Math.min(this.formData.maxLevelCount, (this.formData.steps[0]?.stepNumber ?? 1) + 1);
    const pool: ApprovalConfigApprover = {
      fromStepNumber: 1,
      forStepNumber: nextFor,
      userId: null,
      userName: '',
      roleId: null,
      roleName: '',
      departmentId: null,
      departmentName: '',
      isDefault: false,
      enable: true,
    };
    this.poolTypes.set(pool, 'role');
    this.formData.approvers.push(pool);
  }

  removeApprover(index: number): void {
    this.formData.approvers.splice(index, 1);
  }

  private validateSteps(): string | null {
    for (const step of this.formData.steps) {
      if (step.approverType === 'role' && !step.roleId) {
        return `ชั้นที่ ${step.stepNumber}: กรุณาเลือก Role`;
      }
      if (step.approverType === 'user' && !step.userId) {
        return `ชั้นที่ ${step.stepNumber}: กรุณาเลือกผู้ใช้`;
      }
      if (step.approverType === 'department' && !step.departmentId) {
        return `ชั้นที่ ${step.stepNumber}: กรุณาเลือกแผนก`;
      }
    }
    return null;
  }

  private loadPickerOptions(searchText: string): void {
    const parsed = this.parsePickerKey(this.activePickerKey);
    if (!parsed) return;

    if (parsed.kind === 'role') {
      const q = searchText.trim().toLowerCase();
      let filtered = this.roles.map((r) => ({ id: r.id, label: r.name }));
      if (q) {
        filtered = filtered.filter((r) => r.label.toLowerCase().includes(q));
      }
      this.pickerTotal.set(filtered.length);
      this.pickerOptions.set(filtered.slice(0, ModalApprovalConfigComponent.OPTIONS_PREVIEW));
      this.pickerLoading.set(false);
      return;
    }

    this.pickerLoading.set(true);
    const siteId = this.formData.siteId ?? this.siteState.selectedSite()?.id ?? null;

    if (parsed.kind === 'user') {
      const trimmed = searchText.trim();
      this.settingsApi
        .getAccessUsersForTable(1, ModalApprovalConfigComponent.OPTIONS_PREVIEW, trimmed ? 'login' : undefined, trimmed || undefined)
        .subscribe({
          next: (res) => {
            const rows = res.data ?? (res as { Data?: { id: number; login: string; roleName?: string }[] }).Data ?? [];
            this.pickerOptions.set(
              rows.map((u) => ({
                id: Number(u.id),
                label: String(u.login ?? ''),
                subLabel: u.roleName ? `Role: ${u.roleName}` : undefined,
              }))
            );
            this.pickerTotal.set(Number(res.total ?? (res as { Total?: number }).Total ?? rows.length));
            this.pickerLoading.set(false);
          },
          error: () => {
            this.pickerOptions.set([]);
            this.pickerTotal.set(0);
            this.pickerLoading.set(false);
          },
        });
      return;
    }

    this.settingsApi
      .getDepartmentsForTable(1, ModalApprovalConfigComponent.OPTIONS_PREVIEW, siteId, searchText.trim() || null)
      .subscribe({
        next: (res) => {
          const rows = res.data ?? (res as { Data?: { id: number; name: string; siteName?: string }[] }).Data ?? [];
          this.pickerOptions.set(
            rows.map((d) => ({
              id: Number(d.id),
              label: String(d.name ?? ''),
              subLabel: d.siteName ? `ลาน: ${d.siteName}` : undefined,
            }))
          );
          this.pickerTotal.set(Number(res.total ?? (res as { Total?: number }).Total ?? rows.length));
          this.pickerLoading.set(false);
        },
        error: () => {
          this.pickerOptions.set([]);
          this.pickerTotal.set(0);
          this.pickerLoading.set(false);
        },
      });
  }

  private parsePickerKey(key: string | null): { scope: 'step' | 'pool'; index: number; kind: PickerKind } | null {
    if (!key) return null;
    const match = key.match(/^(step|pool):(\d+):(role|user|department)$/);
    if (!match) return null;
    return {
      scope: match[1] as 'step' | 'pool',
      index: Number(match[2]),
      kind: match[3] as PickerKind,
    };
  }

  private loadDetail(id: number): void {
    this.isLoading.set(true);
    this.api.getApprovalConfigById(id).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapDetail(res as ApprovalConfigDetail & Record<string, unknown>);
        this.syncSteps();
        this.syncPoolTypes();
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.isLoading.set(false);
      },
    });
  }

  private loadLookups(): void {
    this.settingsApi.getMenuListOptions().subscribe({
      next: (res) => (this.menuOptions = res ?? []),
      error: () => (this.menuOptions = []),
    });
    this.settingsApi.getAccessRolesForTable().subscribe({
      next: (res) => {
        this.roles = (res ?? []).map((r) => this.rolesMapper.mapAccessRoleFromDto(r));
      },
      error: () => (this.roles = []),
    });
  }

  private syncSteps(): void {
    const max = Math.min(3, Math.max(1, Number(this.formData.maxLevelCount) || 1));
    this.formData.maxLevelCount = max;
    const current = [...this.formData.steps];
    const steps: ApprovalConfigStep[] = [];
    for (let i = 1; i <= max; i++) {
      const existing = current.find((s) => s.stepNumber === i);
      steps.push(
        existing ?? {
          stepNumber: i,
          stepName: `ชั้นที่ ${i}`,
          approverType: 'role',
          userId: null,
          userName: '',
          roleId: null,
          roleName: '',
          departmentId: null,
          departmentName: '',
          nextApproverMode: i === max ? 'end' : 'fixed',
        }
      );
    }
    this.formData.steps = steps;
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return this.configId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
